import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MessageSquare, FileText, HardHat, CheckCircle } from "lucide-react";
import { Button } from "../ui/button";

const steps = [
  {
    icon: MessageSquare,
    number: "01",
    title: "Erstgespräch & Zielklärung",
    description: "Wir lernen Ihr Projekt kennen, verstehen Ihre Wünsche und definieren gemeinsam die Ziele."
  },
  {
    icon: FileText,
    number: "02",
    title: "Angebot & Terminplan",
    description: "Sie erhalten ein transparentes Angebot mit klarem Terminplan und verbindlichen Kosten."
  },
  {
    icon: HardHat,
    number: "03",
    title: "Umsetzung & Koordination",
    description: "Wir koordinieren alle Gewerke, überwachen die Qualität und halten Sie stets informiert."
  },
  {
    icon: CheckCircle,
    number: "04",
    title: "Abnahme & Übergabe",
    description: "Gemeinsame Abnahme, saubere Übergabe und Ihr zufriedenes Lächeln am Ende."
  }
];

const ProcessSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="ablauf" 
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="process-section"
    >
      {/* Parallax Background */}
      <motion.div 
        className="absolute inset-0 z-0"
        style={{ y: backgroundY }}
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/9405517/pexels-photo-9405517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')"
          }}
        />
        <div className="absolute inset-0 bg-komel-primary/90" />
      </motion.div>

      <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4" data-testid="process-title">
            Alles aus einer Hand – von der Idee bis zur Übergabe
          </h2>
          <div className="w-20 h-1 bg-komel-accent mx-auto" />
        </motion.div>

        {/* Timeline - Desktop */}
        <div className="hidden lg:block relative mb-16">
          {/* Connector Line */}
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-white/20" />
          
          <div className="grid grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative text-center"
                data-testid={`process-step-${index}`}
              >
                {/* Step Circle */}
                <div className="relative z-10 w-32 h-32 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-xl">
                  <step.icon className="w-12 h-12 text-komel-primary" />
                </div>
                
                {/* Number Badge */}
                <div className="absolute top-0 right-1/2 translate-x-16 -translate-y-2 w-10 h-10 bg-komel-accent rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{step.number}</span>
                </div>
                
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  {step.title}
                </h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Timeline - Mobile */}
        <div className="lg:hidden space-y-8 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="flex gap-6"
              data-testid={`process-step-mobile-${index}`}
            >
              {/* Step Number & Icon */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <step.icon className="w-8 h-8 text-komel-primary" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-komel-accent rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-xs">{step.number}</span>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className="w-0.5 h-8 bg-white/20 mx-auto mt-4" />
                )}
              </div>
              
              {/* Content */}
              <div className="pt-2">
                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                  {step.title}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center"
        >
          <Button
            onClick={() => scrollToSection("#kontakt")}
            className="bg-komel-accent hover:bg-komel-accent-hover text-white font-semibold px-10 py-6 text-lg rounded-none transition-all duration-300 hover:scale-105"
            data-testid="process-cta"
          >
            Unverbindliches Erstgespräch
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;
